import { supabase } from '../config/supabase';

export interface ProgressStage {
  value: number;
  label: string;
  description: string;
  color?: string;
}

// Default progress stages (fallback)
const DEFAULT_PROGRESS_STAGES: ProgressStage[] = [
  { 
    value: 0, 
    label: 'Invitation Sent', 
    description: 'Initial invitation has been sent to the candidate',
    color: '#ff6b6b'
  },
  { 
    value: 1, 
    label: 'Pending Enrollment', 
    description: 'Candidate is pending enrollment in the course',
    color: '#feca57'
  },
  { 
    value: 2, 
    label: 'Enrolled in Course', 
    description: 'Candidate is currently enrolled in the course',
    color: '#48dbfb'
  },
  { 
    value: 3, 
    label: 'Passed the Test Waiting License Approval', 
    description: 'Candidate passed the test and is waiting for license approval',
    color: '#ff9ff3'
  },
  { 
    value: 4, 
    label: 'Company Onboarding Waiting Agent Number Approval', 
    description: 'Company onboarding in progress, waiting for agent number approval',
    color: '#54a0ff'
  },
  { 
    value: 5, 
    label: 'Company Onboarding Agent Number Approved', 
    description: 'Agent number has been approved, onboarding continues',
    color: '#5f27cd'
  },
  { 
    value: 6, 
    label: 'Agent Number Approved Waiting for Training', 
    description: 'Agent number approved, waiting for training to begin',
    color: '#00d2d3'
  }
];

// Organization-specific progress stages
const ORG_PROGRESS_STAGES: { [key: number]: ProgressStage[] } = {
  1: DEFAULT_PROGRESS_STAGES,
  2: [
    { 
      value: 0, 
      label: 'Invitation Sent', 
      description: 'Initial invitation has been sent to the candidate',
      color: '#ff6b6b'
    },
    { 
      value: 1, 
      label: 'Pending Enrollment', 
      description: 'Candidate is pending enrollment in the course',
      color: '#feca57'
    },
    { 
      value: 2, 
      label: 'Enrolled in Course', 
      description: 'Candidate is currently enrolled in the course',
      color: '#48dbfb'
    },
    { 
      value: 3, 
      label: 'Passed the Test Waiting License Approval', 
      description: 'Candidate passed the test and is waiting for license approval',
      color: '#ff9ff3'
    },
    { 
      value: 4, 
      label: 'Company Onboarding Waiting Agent Number Approval', 
      description: 'Company onboarding in progress, waiting for agent number approval',
      color: '#54a0ff'
    },
    { 
      value: 5, 
      label: 'Company Onboarding', 
      description: 'Agent number has been approved, onboarding continues',
      color: '#5f27cd'
    },
    { 
      value: 6, 
      label: 'Agent Number Pending Waiting for Training', 
      description: 'Agent number approved, waiting for training to begin',
      color: '#00d2d3'
    }
  ],
  3: [
    { 
      value: 0, 
      label: 'Invitation Sent', 
      description: 'Initial invitation has been sent to the candidate',
      color: '#ff6b6b'
    },
    { 
      value: 1, 
      label: 'Pending Enrollment', 
      description: 'Candidate is pending enrollment in the course',
      color: '#feca57'
    },
    { 
      value: 2, 
      label: 'Enrolled in Course', 
      description: 'Candidate is currently enrolled in the course',
      color: '#48dbfb'
    },
    { 
      value: 3, 
      label: 'Passed the Test Waiting License Approval', 
      description: 'Candidate passed the test and is waiting for license approval',
      color: '#ff9ff3'
    },
    { 
      value: 4, 
      label: 'Company Onboarding Waiting Agent Number Approval', 
      description: 'Company onboarding in progress, waiting for agent number approval',
      color: '#54a0ff'
    },
    { 
      value: 5, 
      label: 'Company Onboarding', 
      description: 'Agent number has been approved, onboarding continues',
      color: '#5f27cd'
    },
    { 
      value: 6, 
      label: 'Agent Number Pending Waiting for Training', 
      description: 'Agent number approved, waiting for training to begin',
      color: '#00d2d3'
    }
  ]
};

export const progressStageService = {
  /**
   * Get progress stages for a specific organization
   */
  getProgressStages(orgId: number): ProgressStage[] {
    return ORG_PROGRESS_STAGES[orgId] || DEFAULT_PROGRESS_STAGES;
  },

  /**
   * Get progress stages for the current user's organization
   */
  async getCurrentUserProgressStages(): Promise<ProgressStage[]> {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        console.warn('User not authenticated, using default progress stages');
        return DEFAULT_PROGRESS_STAGES;
      }

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('id', user.id)
        .single();

      if (profileError || !profile) {
        console.warn('Could not fetch user profile, using default progress stages');
        return DEFAULT_PROGRESS_STAGES;
      }

      return this.getProgressStages(Number(profile.org_id));
    } catch (error) {
      console.error('Error fetching user progress stages:', error);
      return DEFAULT_PROGRESS_STAGES;
    }
  },

  /**
   * Try DB override first (progress_stage_settings), then fallback to existing behavior.
   */
  async getCurrentUserProgressStagesWithDbOverride(): Promise<ProgressStage[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return DEFAULT_PROGRESS_STAGES;
      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('id', user.id)
        .maybeSingle();
      const orgId = Number(profile?.org_id || 0);

      // Attempt to load from progress_stage_settings
      const { data, error } = await supabase
        .from('progress_stage_settings')
        .select('*')
        .eq('org_id', orgId)
        .order('order_index', { ascending: true });

      if (!error && data && data.length > 0) {
        return data.map((row: any) => ({
          value: Number(row.value),
          label: row.label,
          description: row.description || '',
          color: row.color || undefined,
        }));
      }

      // Fallback to existing org-based configuration
      return this.getProgressStages(orgId);
    } catch (e) {
      return DEFAULT_PROGRESS_STAGES;
    }
  },

  /**
   * Get stage label by progress value for a specific organization
   */
  getStageLabel(progress: number, orgId: number): string {
    const stages = this.getProgressStages(orgId);
    const stage = stages.find(s => s.value === progress);
    return stage?.label || 'Unknown Stage';
  },

  /**
   * Get stage description by progress value for a specific organization
   */
  getStageDescription(progress: number, orgId: number): string {
    const stages = this.getProgressStages(orgId);
    const stage = stages.find(s => s.value === progress);
    return stage?.description || 'No description available';
  },

  /**
   * Get stage color by progress value for a specific organization
   */
  getStageColor(progress: number, orgId: number): string {
    const stages = this.getProgressStages(orgId);
    const stage = stages.find(s => s.value === progress);
    return stage?.color || '#666666';
  },

  /**
   * Get available target stages for a hire (excluding current stage)
   */
  getAvailableTargetStages(currentProgress: number, orgId: number): ProgressStage[] {
    const stages = this.getProgressStages(orgId);
    return stages.filter(stage => stage.value !== currentProgress);
  },

  /**
   * Validate if a move is allowed
   */
  validateMove(currentProgress: number, targetProgress: number): { valid: boolean; message?: string } {
    if (currentProgress === targetProgress) {
      return { valid: false, message: 'Cannot move to the same stage' };
    }

    if (targetProgress < 0 || targetProgress > 6) {
      return { valid: false, message: 'Invalid target progress stage' };
    }

    return { valid: true };
  },

  /**
   * Reverse mapping from progress to checkListNum based on OnboardingSteps.tsx algorithm
   */
  getCheckListNumForProgress(progress: number): string | null {
    switch (progress) {
      case 0: return '1'; // Invitation Sent
      case 1: return '1'; // Pending Enrollment (same as 0)
      case 2: return '2'; // Enrolled in Course
      case 3: return '3'; // Passed Test Waiting License Approval
      case 4: return '4'; // Company Onboarding Waiting Agent Number
      case 5: return '9'; // Company Onboarding Agent Number Approved
      case 6: return '14'; // Agent Number Approved Waiting for Training
      default: return null;
    }
  }
}; 